import { Component, Inject, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { User } from '@app/types';
import { Role } from '@app/types/role.type';
import { RoleService } from '@app/services/entities/role.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-user-detail-dialog',
    standalone: true,
    imports: [CommonModule, MaterialModule],
    templateUrl: './user-detail-dialog.component.html',
    styleUrl: './user-detail-dialog.component.scss'
})
export class UserDetailDialogComponent {
    private dialogRef = inject(MatDialogRef<UserDetailDialogComponent>);
    private roleService = inject(RoleService);
    private snackBar = inject(MatSnackBar);
    public user: User = inject(MAT_DIALOG_DATA);

    isSaving = signal(false);

    allRoles = this.roleService.rolesSignal;

    userRoles = computed<Role[]>(() => {
        const roles = this.allRoles() || [];
        return roles.filter(role => role.users && role.users.includes(this.user.id) && !role.deleted);
    });

    availableRoles = computed<Role[]>(() => {
        const roles = this.allRoles() || [];
        const assigned = new Set(this.userRoles().map(r => r.id));
        return roles.filter(role => !role.deleted && !assigned.has(role.id));
    });

    /** Unión de permisos de todos los roles asignados (sin duplicados) */
    uniquePermissions = computed<string[]>(() => {
        const perms = new Set<string>();
        for (const role of this.userRoles()) {
            for (const p of role.permissions || []) perms.add(p);
        }
        return Array.from(perms).sort();
    });

    hasRole(role: Role): boolean {
        return this.userRoles().some(r => r.id === role.id);
    }

    toggleRole(role: Role): void {
        if (this.isSaving()) return;
        this.isSaving.set(true);

        const wasAssigned = this.hasRole(role);
        const op = wasAssigned
            ? this.roleService.removeUserFromRole(role.id, this.user.id)
            : this.roleService.assignUserToRole(role.id, this.user.id);

        op.subscribe({
            next: () => {
                this.isSaving.set(false);
                this.snackBar.open(
                    wasAssigned ? `Rol "${role.name}" removido` : `Rol "${role.name}" agregado`,
                    'Cerrar',
                    { duration: 2500 }
                );
            },
            error: () => {
                this.isSaving.set(false);
                this.snackBar.open('Error al actualizar el rol', 'Cerrar', { duration: 3000 });
            }
        });
    }

    onClose(): void {
        this.dialogRef.close();
    }
}
